import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Subject, Observable, of, concat } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { UserProfile, UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { Organization } from '@app/modules/core/store/models/organization.model';
import * as fromAuth from '../../store/auth/';
import { AuthToken, UserContext } from '../../store/auth/';
import produce from 'immer';
import { AuthApiService } from '../../store/auth/auth-api.service';
import { AcceptancePolicy } from '@app/modules/management/store/organizations';

@Component({
    selector: 'topo-policy-acceptance',
    templateUrl: './policy-acceptance.component.html',
    styleUrls: ['./policy-acceptance.component.scss'],
    standalone: false
})
export class PolicyAcceptanceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  userProfile: UserProfile;
  organization: Organization;
  userContext: UserContext;
  sections: { uId: string; text: string; checked: boolean }[] = [];
  showPage: boolean = true;

  constructor(private store: Store<any>, private profileApiService: UserProfileApiService, private authApiService: AuthApiService) {}

  ngOnInit(): void {
    this.store
      .select(fromAuth.selectShowAcceptancePage)
      .pipe(
        takeUntil(this.destroy$),
        map(showPage => {
          this.showPage = showPage;
        })
      )
      .subscribe();

    this.store
      .select(fromUserProfile.selectProfileUser)
      .pipe(
        takeUntil(this.destroy$),
        map(userProfile => {
          this.userProfile = userProfile;
          if (!!this.userProfile && !!this.organization) {
            this.buildSections();
          }
        })
      )
      .subscribe();

    this.store
      .select(fromUserProfile.selectOrganization)
      .pipe(
        takeUntil(this.destroy$),
        map(organization => {
          this.organization = organization;
          if (!!this.userProfile && !!this.organization) {
            this.buildSections();
          }
        })
      )
      .subscribe();

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(
        takeUntil(this.destroy$),
        map(userContext => {
          this.userContext = userContext;
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildSections(): void {
    this.sections = [];

    let policy = this.organization?.configurationSettings.acceptancePolicy;
    if (!!policy) {
      policy = policy as AcceptancePolicy;
    }

    let policySectionsAccepted = this.userProfile?.settings?.policySectionsAccepted;

    const orgCulture = this.organization?.culture?.cultureCode;

    policy?.sections?.forEach(s => {
      const text = s.text[this.userProfile.culture?.cultureCode] ?? s.text[orgCulture];
      if (!!text) {
        this.sections.push({ uId: s.uId, text: text, checked: policySectionsAccepted?.includes(s.uId) });
      }
    });
  }

  onSectionChecked(section: { text: string; checked: boolean }): void {
    section.checked = !section.checked;
  }

  allChecked(): boolean {
    return !this.sections?.some(s => !s.checked);
  }

  close(): void {}

  continueClick(): void {
    const policySectionsAccepted = [];
    this.sections?.forEach(s => policySectionsAccepted.push(s.uId));

    const userProfile = produce(this.userProfile, draftState => {
      if (!!!draftState.settings) {
        draftState.settings = {};
      }

      draftState.settings.policySectionsAccepted = policySectionsAccepted;
    });

    // update user profile with acceptedPolicy, get new token and continue to after login route
    this.profileApiService
      .updateUser(this.userContext.organizationUId, userProfile)
      .pipe(take(1))
      .subscribe(res => {
        this.store.dispatch(fromUserProfile.getUserProfileComplete({ result: res }));
        this.authApiService
          .refreshAccessToken()
          .pipe(take(1))
          .subscribe(token => {
            if (!!token) {
              this.store.dispatch(fromAuth.setToken({ token }));
              const userContext = new UserContext(token.access_token);
              this.showPage = false;
              this.store.dispatch(fromAuth.routeAfterLoginSuccess({ userContext: userContext, checkPolicy: false }));
            }
          });
      });
  }
}
